import React from "react";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ANALYTICS_INTERVALS, ANALYTICS_RANGE_PRESETS, ANALYTICS_SUBTABS } from "../adminConstants";
import { formatCurrency, formatCurrencyCompact, formatPercent } from "../adminUtils";

const DEVICE_BAR_SIZE = 60;

const DeviceBarCursor = ({ x, y, width, height }) => {
  if (
    typeof x !== "number" ||
    typeof y !== "number" ||
    typeof width !== "number" ||
    typeof height !== "number"
  ) {
    return null;
  }

  const cursorWidth = Math.min(DEVICE_BAR_SIZE, width);
  const offset = Math.max(0, (width - cursorWidth) / 2);

  return (
    <rect
      x={x + offset}
      y={y}
      width={cursorWidth}
      height={height}
      rx={10}
      fill="rgba(15, 45, 46, 0.06)"
    />
  );
};

const AdminAnalyticsTab = ({
  t,
  isActive,
  analyticsRangePreset,
  setAnalyticsRangePreset,
  analyticsCustomFrom,
  setAnalyticsCustomFrom,
  analyticsCustomTo,
  setAnalyticsCustomTo,
  analyticsGranularity,
  setAnalyticsGranularity,
  analyticsLocationId,
  setAnalyticsLocationId,
  analyticsLocationOptions,
  analyticsProductTypeId,
  setAnalyticsProductTypeId,
  analyticsProductTypeOptions,
  analyticsCompareEnabled,
  setAnalyticsCompareEnabled,
  analyticsCompareFrom,
  setAnalyticsCompareFrom,
  analyticsCompareTo,
  setAnalyticsCompareTo,
  analyticsSubTab,
  setAnalyticsSubTab,
  analyticsRankTab,
  analyticsRankRows,
  analyticsRankMax,
  analyticsRangeLabel,
  analyticsLoading,
  analyticsError,
  analyticsHasData,
  analyticsKpis,
  analyticsChartData,
  analyticsBookingsChartData,
  analyticsCompareActive,
  analyticsCurrency,
  analyticsUsageChartData,
  analyticsUsageByEquipment,
  analyticsHeatmapSlots,
  analyticsHeatmapDays,
  analyticsHeatmapMap,
  analyticsHeatmapMax,
  analyticsAttendanceSeries,
  analyticsAttendanceTotals,
  analyticsFunnel,
  analyticsRefundReasons,
  analyticsPartnerReliability,
  analyticsLateReturns,
  analyticsNoShows,
  analyticsPickupDelay,
  handleExportAnalytics,
  handleExportAnalyticsAccountant,
  handleAnalyticsChartClick,
  buildSparkline,
}) => (
<div
              className={`admin-tab ${
                isActive ? "admin-tab--active" : ""
              }`.trim()}
            >
              <section className="admin-section admin-analytics admin-animate admin-animate--delay-1">
                <header>
                  <div>
                    <p className="admin-section__eyebrow">
                      {t("admin.analytics.eyebrow", "Performance intelligence")}
                    </p>
                    <h2>{t("admin.analytics.heading", "Analytics dashboard")}</h2>
                  </div>
                </header>
                <div className="admin-analytics__filterbar">
                  <div className="admin-analytics__filter">
                    <label className="admin-form__label" htmlFor="analyticsRange">
                      Date range
                    </label>
                    <select
                      id="analyticsRange"
                      className="admin-select admin-select--compact"
                      value={analyticsRangePreset}
                      onChange={(event) => setAnalyticsRangePreset(event.target.value)}
                    >
                      {ANALYTICS_RANGE_PRESETS.map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {analyticsRangePreset === "custom" && (
                    <div className="admin-analytics__filter admin-analytics__filter--custom">
                      <label className="admin-form__label">Custom</label>
                      <div className="admin-analytics__range">
                        <input
                          type="date"
                          className="admin-input admin-input--compact"
                          value={analyticsCustomFrom}
                          onChange={(event) => setAnalyticsCustomFrom(event.target.value)}
                        />
                        <span className="admin-analytics__range-divider">→</span>
                        <input
                          type="date"
                          className="admin-input admin-input--compact"
                          value={analyticsCustomTo}
                          onChange={(event) => setAnalyticsCustomTo(event.target.value)}
                        />
                      </div>
                    </div>
                  )}
                  <div className="admin-analytics__filter admin-analytics__filter--granularity">
                    <label className="admin-form__label">Granularity</label>
                    <div className="admin-analytics__toggle-group">
                      {ANALYTICS_INTERVALS.map((interval) => (
                        <button
                          key={interval.id}
                          type="button"
                          className={`admin-toggle ${
                            analyticsGranularity === interval.id
                              ? "admin-toggle--active"
                              : ""
                          }`.trim()}
                          onClick={() => setAnalyticsGranularity(interval.id)}
                        >
                          {interval.labelFallback}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="admin-analytics__filter">
                    <label className="admin-form__label" htmlFor="analyticsLocation">
                      Location
                    </label>
                    <select
                      id="analyticsLocation"
                      className="admin-select admin-select--compact"
                      value={analyticsLocationId}
                      onChange={(event) => setAnalyticsLocationId(event.target.value)}
                    >
                      {analyticsLocationOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="admin-analytics__filter">
                    <label className="admin-form__label" htmlFor="analyticsProduct">
                      Product type
                    </label>
                    <select
                      id="analyticsProduct"
                      className="admin-select admin-select--compact"
                      value={analyticsProductTypeId}
                      onChange={(event) => setAnalyticsProductTypeId(event.target.value)}
                    >
                      {analyticsProductTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="admin-analytics__filter admin-analytics__filter--compare">
                    <div className="admin-analytics__filter-head">
                      <label className="admin-form__label">Compare range</label>
                      <label className="admin-analytics__switch">
                        <input
                          type="checkbox"
                          checked={analyticsCompareEnabled}
                          onChange={(event) => setAnalyticsCompareEnabled(event.target.checked)}
                        />
                      </label>
                    </div>
                    <div className="admin-analytics__compare">
                      <div className="admin-analytics__range">
                        <input
                          type="date"
                          className="admin-input admin-input--compact"
                          value={analyticsCompareFrom}
                          onChange={(event) => setAnalyticsCompareFrom(event.target.value)}
                          disabled={!analyticsCompareEnabled}
                        />
                        <span className="admin-analytics__range-divider">→</span>
                        <input
                          type="date"
                          className="admin-input admin-input--compact"
                          value={analyticsCompareTo}
                          onChange={(event) => setAnalyticsCompareTo(event.target.value)}
                          disabled={!analyticsCompareEnabled}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="admin-analytics__filter admin-analytics__filter--export">
                    <label className="admin-form__label">Export</label>
                    <div className="admin-analytics__export-actions">
                      <button
                        type="button"
                        className="admin-button admin-button--outline"
                        onClick={() => handleExportAnalytics("csv")}
                      >
                        CSV
                      </button>
                      <button
                        type="button"
                        className="admin-button admin-button--outline"
                      onClick={() => handleExportAnalytics("pdf")}
                    >
                      PDF
                    </button>
                      <button
                        type="button"
                        className="admin-button admin-button--outline"
                        onClick={handleExportAnalyticsAccountant}
                      >
                        Accountant pack
                      </button>
                    </div>
                  </div>
                </div>
                {analyticsLoading ? (
                  <p className="admin-empty">
                    {t("admin.analytics.loading", "Loading analytics...")}
                  </p>
                ) : analyticsError ? (
                  <p className="admin-empty">{analyticsError}</p>
                ) : (
                  <>
                    <div className="admin-analytics__kpis">
                      {analyticsKpis.map((card) => (
                        <div
                          key={card.id}
                          className="admin-kpi"
                        >
                          <div className="admin-kpi__head">
                            <span className="admin-kpi__label">{card.label}</span>
                            <span
                              className={`admin-kpi__delta admin-kpi__delta--${
                                analyticsCompareActive ? card.delta.tone : "muted"
                              }`.trim()}
                            >
                              {analyticsCompareActive ? card.delta.label : "—"}
                            </span>
                          </div>
                          <span className="admin-kpi__value">{card.value}</span>
                          {buildSparkline(card.sparkline)}
                        </div>
                      ))}
                    </div>
                    <div className="admin-analytics__subtabs">
                      {ANALYTICS_SUBTABS.map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          className={`admin-toggle ${
                            analyticsSubTab === tab.id ? "admin-toggle--active" : ""
                          }`.trim()}
                          onClick={() => setAnalyticsSubTab(tab.id)}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                    {analyticsSubTab === "revenue" && (
                      <div className="admin-analytics__grid">
                        <article className="admin-analytics__card admin-analytics__card--wide">
                          <div className="admin-analytics__card-header">
                            <div>
                              <h3>Revenue</h3>
                              <p>{analyticsRangeLabel}</p>
                            </div>
                          </div>
                          {analyticsHasData ? (
                            <div className="admin-chart__canvas">
                              <ResponsiveContainer width="100%" height={260}>
                                <ComposedChart
                                  data={analyticsChartData}
                                  margin={{ top: 10, right: 24, left: 0, bottom: 0 }}
                                  onClick={handleAnalyticsChartClick}
                                >
                                  <defs>
                                    <linearGradient
                                      id="analyticsGross"
                                      x1="0"
                                      y1="0"
                                      x2="0"
                                      y2="1"
                                    >
                                      <stop offset="5%" stopColor="#208E76" stopOpacity={0.45} />
                                      <stop offset="95%" stopColor="#208E76" stopOpacity={0} />
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid
                                    strokeDasharray="4 6"
                                    stroke="rgba(15, 45, 46, 0.12)"
                                  />
                                  <XAxis
                                    dataKey="label"
                                    tickLine={false}
                                    axisLine={false}
                                    interval="preserveStartEnd"
                                    tick={{ fontSize: 12, fill: "#6B7C7D" }}
                                  />
                                  <YAxis
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 12, fill: "#6B7C7D" }}
                                    tickFormatter={(value) =>
                                      formatCurrencyCompact(value, analyticsCurrency)
                                    }
                                  />
                                  <Tooltip
                                    isAnimationActive={false}
                                    cursor={{ stroke: "rgba(15, 45, 46, 0.2)", strokeWidth: 1 }}
                                    content={({ active, payload }) => {
                                      if (!active || !payload?.length) return null;
                                      const row = payload[0]?.payload;
                                      if (!row) return null;
                                      const avgValue =
                                        row.bookings > 0
                                          ? row.gross / row.bookings
                                          : 0;
                                      return (
                                        <div className="admin-chart__tooltip">
                                          <p className="admin-chart__tooltip-title">{row.label}</p>
                                          <p className="admin-chart__tooltip-value">
                                            Gross: {formatCurrency(row.gross, analyticsCurrency)}
                                          </p>
                                          <p className="admin-chart__tooltip-meta">
                                            Net: {formatCurrency(row.net, analyticsCurrency)} ·
                                            Refunds:{" "}
                                            {formatCurrency(
                                              Math.abs(row.refunds),
                                              analyticsCurrency
                                            )}
                                          </p>
                                          <p className="admin-chart__tooltip-meta">
                                            Bookings: {row.bookings} · Avg value:{" "}
                                            {formatCurrency(avgValue, analyticsCurrency)}
                                          </p>
                                        </div>
                                      );
                                    }}
                                  />
                                  <Legend verticalAlign="top" height={24} />
                                  <Bar dataKey="refunds" fill="#CE6F3F" name="Refunds" />
                                  <Area
                                    type="monotone"
                                    dataKey="gross"
                                    stroke="#208E76"
                                    fill="url(#analyticsGross)"
                                    strokeWidth={2.4}
                                    dot={false}
                                    name="Gross"
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="net"
                                    stroke="#a400f6"
                                    strokeWidth={2}
                                    dot={false}
                                    name="Net"
                                  />
                                  {analyticsCompareActive && (
                                    <>
                                      <Line
                                        type="monotone"
                                        dataKey="prevGross"
                                        stroke="#208E76"
                                        strokeWidth={1.4}
                                        strokeDasharray="4 4"
                                        dot={false}
                                        name="Prev gross"
                                        opacity={0.4}
                                      />
                                      <Line
                                        type="monotone"
                                        dataKey="prevNet"
                                        stroke="#1F5AA6"
                                        strokeWidth={1.4}
                                        strokeDasharray="4 4"
                                        dot={false}
                                        name="Prev net"
                                        opacity={0.4}
                                      />
                                    </>
                                  )}
                                </ComposedChart>
                              </ResponsiveContainer>
                            </div>
                          ) : (
                            <p className="admin-empty">
                              {t("admin.analytics.empty", "No booking activity for this period.")}
                            </p>
                          )}
                        </article>
                        <article className="admin-analytics__card">
                          <div className="admin-analytics__card-header">
                            <div>
                              <h3>Bookings</h3>
                              <p>Volume by status</p>
                            </div>
                          </div>
                          <div className="admin-chart__canvas">
                            <ResponsiveContainer width="100%" height={240}>
                              <BarChart data={analyticsBookingsChartData}>
                                <CartesianGrid
                                  strokeDasharray="4 6"
                                  stroke="rgba(15, 45, 46, 0.12)"
                                />
                                <XAxis
                                  dataKey="label"
                                  tickLine={false}
                                  axisLine={false}
                                  interval="preserveStartEnd"
                                  tick={{ fontSize: 12, fill: "#6B7C7D" }}
                                />
                                <YAxis
                                  tickLine={false}
                                  axisLine={false}
                                  tick={{ fontSize: 12, fill: "#6B7C7D" }}
                                />
                                <Tooltip
                                  isAnimationActive={false}
                                  content={({ active, payload }) => {
                                    if (!active || !payload?.length) return null;
                                    const row = payload[0]?.payload;
                                    if (!row) return null;
                                    return (
                                      <div className="admin-chart__tooltip">
                                        <p className="admin-chart__tooltip-title">{row.label}</p>
                                        <p className="admin-chart__tooltip-value">
                                          Completed: {row.completed}
                                        </p>
                                        <p className="admin-chart__tooltip-meta">
                                          Cancelled: {row.cancelled} · Refunded: {row.refunded}
                                        </p>
                                      </div>
                                    );
                                  }}
                                />
                                <Legend verticalAlign="top" height={24} />
                                <Bar
                                  dataKey="completed"
                                  stackId="status"
                                  fill="#208E76"
                                  name="Completed"
                                />
                                <Bar
                                  dataKey="cancelled"
                                  stackId="status"
                                  fill="#CE6F3F"
                                  name="Cancelled"
                                />
                                <Bar
                                  dataKey="refunded"
                                  stackId="status"
                                  fill="#6D77A1"
                                  name="Refunded"
                                />
                                {analyticsCompareActive && (
                                  <Line
                                    type="monotone"
                                    dataKey="prevTotal"
                                    stroke="#0F2D2E"
                                    strokeDasharray="4 4"
                                    dot={false}
                                    name="Prev total"
                                    opacity={0.4}
                                  />
                                )}
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </article>
                        <article className="admin-analytics__card">
                          <div className="admin-analytics__card-header">
                            <div>
                              <h3>Revenue by location</h3>
                            </div>
                          </div>
                          <ul className="admin-analytics__rank-list">
                            {analyticsRankRows.length === 0 ? (
                              <li className="admin-empty">No ranked data yet.</li>
                            ) : (
                              analyticsRankRows.map((row) => {
                                const revenue = row.revenue ?? 0;
                                const bookingsCount = row.bookingCount ?? 0;
                                const avgValue = bookingsCount > 0 ? revenue / bookingsCount : 0;
                                const label =
                                  analyticsRankTab === "partner"
                                    ? row.partnerName
                                    : row.locationName;
                                const targetId =
                                  analyticsRankTab === "partner"
                                    ? row.partnerId
                                    : row.locationId;
                                const canFilterLocation = analyticsRankTab !== "partner";
                                return (
                                  <li
                                    key={`${analyticsRankTab}-${targetId}`}
                                    className="admin-analytics__rank-row"
                                    onClick={() => {
                                      if (canFilterLocation) {
                                        setAnalyticsLocationId(targetId);
                                      }
                                    }}
                                    role={canFilterLocation ? "button" : undefined}
                                    tabIndex={canFilterLocation ? 0 : -1}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter" && canFilterLocation) {
                                        setAnalyticsLocationId(targetId);
                                      }
                                    }}
                                  >
                                    <div className="admin-analytics__rank-info">
                                      <span>{label}</span>
                                      <small>
                                        {bookingsCount} bookings ·{" "}
                                        {formatCurrency(avgValue, analyticsCurrency)}
                                      </small>
                                    </div>
                                    <div className="admin-analytics__rank-bar">
                                      <span
                                        style={{
                                          width: `${Math.round(
                                            (revenue / analyticsRankMax) * 100
                                          )}%`,
                                        }}
                                      />
                                    </div>
                                    <span className="admin-analytics__rank-value">
                                      {formatCurrency(revenue, analyticsCurrency)}
                                    </span>
                                  </li>
                                );
                              })
                            )}
                          </ul>
                        </article>
                        <article className="admin-analytics__card">
                          <div className="admin-analytics__card-header">
                            <div>
                              <h3>Time slot heatmap</h3>
                              <p>Bookings by hour block</p>
                            </div>
                          </div>
                          <div className="admin-analytics__heatmap">
                            <div className="admin-analytics__heatmap-legend">
                              <span>Low</span>
                              <div className="admin-analytics__heatmap-scale">
                                {[0.12, 0.28, 0.44, 0.6, 0.76].map((alpha, index) => (
                                  <span
                                    key={`heatmap-step-${index}`}
                                    style={{ background: `rgba(32, 142, 118, ${alpha})` }}
                                  />
                                ))}
                              </div>
                              <span>High</span>
                            </div>
                            <div className="admin-analytics__heatmap-scroll">
                              <div
                                className="admin-analytics__heatmap-grid"
                                style={{ "--heatmap-columns": analyticsHeatmapSlots.length || 1 }}
                              >
                                <div className="admin-analytics__heatmap-row admin-analytics__heatmap-row--header">
                                  <span className="admin-analytics__heatmap-corner">Day</span>
                                  {analyticsHeatmapSlots.map((slot) => (
                                    <span key={slot}>{slot}</span>
                                  ))}
                                </div>
                                {analyticsHeatmapDays.map((day, dayIndex) => (
                                  <div key={day} className="admin-analytics__heatmap-row">
                                    <span className="admin-analytics__heatmap-day">{day}</span>
                                    {analyticsHeatmapSlots.map((slot) => {
                                      const count =
                                        analyticsHeatmapMap.get(`${dayIndex}-${slot}`) || 0;
                                      const intensity = analyticsHeatmapMax
                                        ? count / analyticsHeatmapMax
                                        : 0;
                                      const background = `rgba(32, 142, 118, ${
                                        0.08 + intensity * 0.78
                                      })`;
                                      const color = intensity > 0.55 ? "#F7FBFA" : "#0F2D2E";
                                      return (
                                        <span
                                          key={`${day}-${slot}`}
                                          className="admin-analytics__heatmap-cell"
                                          style={{ background, color }}
                                          title={`${day} ${slot} · ${count} bookings`}
                                        >
                                          {count > 0 ? count : "-"}
                                        </span>
                                      );
                                    })}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </article>
                        <article className="admin-analytics__card">
                          <div className="admin-analytics__card-header">
                            <div>
                              <h3>Funnel</h3>
                              <p>Home page → payment → completed</p>
                            </div>
                          </div>
                          {analyticsFunnel ? (
                            <div className="admin-analytics__funnel">
                              {[
                                {
                                  label: "Home page",
                                  value:
                                    analyticsFunnel.homePageCount ??
                                    analyticsFunnel.createdCount ??
                                    0,
                                },
                                { label: "Bookings page", value: analyticsFunnel.bookingsPageCount ?? 0 },
                                {
                                  label: "Booking day/night",
                                  value: analyticsFunnel.bookingDayNightCount ?? 0,
                                },
                                { label: "Location", value: analyticsFunnel.locationCount ?? 0 },
                                { label: "Equipment", value: analyticsFunnel.equipmentCount ?? 0 },
                                {
                                  label: "Date time picker",
                                  value: analyticsFunnel.dateTimePickerCount ?? 0,
                                },
                                {
                                  label: "Payment page",
                                  value:
                                    analyticsFunnel.paymentPageCount ??
                                    analyticsFunnel.createdCount ??
                                    0,
                                },
                                { label: "Paid", value: analyticsFunnel.paidCount ?? 0 },
                                { label: "Completed", value: analyticsFunnel.completedCount ?? 0 },
                              ].map((step) => {
                                const max = (
                                  analyticsFunnel.homePageCount ??
                                  analyticsFunnel.createdCount ??
                                  0
                                ) || 1;
                                return (
                                  <div key={step.label} className="admin-analytics__funnel-step">
                                    <span>{step.label}</span>
                                    <div className="admin-analytics__funnel-bar">
                                      <span
                                        style={{
                                          width: `${Math.round((step.value / max) * 100)}%`,
                                        }}
                                      />
                                    </div>
                                    <strong>{step.value}</strong>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="admin-empty">No funnel data yet.</p>
                          )}
                        </article>
                      </div>
                    )}
                    {analyticsSubTab === "utilization" && (
                      <div className="admin-analytics__grid">
                        <article className="admin-analytics__card admin-analytics__card--wide">
                          <div className="admin-analytics__card-header">
                            <div>
                              <h3>Equipment usage</h3>
                            </div>
                          </div>
                          <div className="admin-chart__canvas">
                            <ResponsiveContainer width="100%" height={240}>
                              <LineChart data={analyticsUsageChartData}>
                                <CartesianGrid
                                  strokeDasharray="4 6"
                                  stroke="rgba(15, 45, 46, 0.12)"
                                />
                                <XAxis
                                  dataKey="label"
                                  tickLine={false}
                                  axisLine={false}
                                  interval="preserveStartEnd"
                                  tick={{ fontSize: 12, fill: "#6B7C7D" }}
                                />
                                <YAxis
                                  tickLine={false}
                                  axisLine={false}
                                  tick={{ fontSize: 12, fill: "#6B7C7D" }}
                                />
                                <Tooltip isAnimationActive={false} />
                                <Line
                                  type="monotone"
                                  dataKey="usage"
                                  stroke="#176D7A"
                                  strokeWidth={2.2}
                                  dot={false}
                                  name="Usage"
                                />
                                {analyticsCompareActive && (
                                  <Line
                                    type="monotone"
                                    dataKey="prevUsage"
                                    stroke="#176D7A"
                                    strokeDasharray="4 4"
                                    dot={false}
                                    name="Prev usage"
                                    opacity={0.4}
                                  />
                                )}
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </article>
                        <article className="admin-analytics__card">
                          <div className="admin-analytics__card-header">
                            <div>
                              <h3>Usage by equipment</h3>
                            </div>
                          </div>
                          <div className="admin-chart__canvas">
                            <ResponsiveContainer width="100%" height={240}>
                              <BarChart data={analyticsUsageByEquipment} layout="vertical">
                                <CartesianGrid
                                  strokeDasharray="4 6"
                                  stroke="rgba(15, 45, 46, 0.12)"
                                />
                                <XAxis type="number" tickLine={false} axisLine={false} />
                                <YAxis
                                  type="category"
                                  dataKey="name"
                                  width={90}
                                  tickLine={false}
                                  axisLine={false}
                                  tick={{ fontSize: 12, fill: "#6B7C7D" }}
                                />
                                <Tooltip isAnimationActive={false} />
                                <Bar dataKey="usage" fill="#208E76" radius={[6, 6, 6, 6]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </article>
                      </div>
                    )}
                    {analyticsSubTab === "operations" && (
                      <div className="admin-analytics__grid">
                        <article className="admin-analytics__card">
                          <div className="admin-analytics__card-header">
                            <div>
                              <h3>Late returns</h3>
                            </div>
                          </div>
                          <div className="admin-chart__canvas">
                            <ResponsiveContainer width="100%" height={220}>
                              <LineChart data={analyticsLateReturns}>
                                <CartesianGrid
                                  strokeDasharray="4 6"
                                  stroke="rgba(15, 45, 46, 0.12)"
                                />
                                <XAxis dataKey="label" tickLine={false} axisLine={false} />
                                <YAxis tickLine={false} axisLine={false} />
                                <Tooltip isAnimationActive={false} />
                                <Line
                                  type="monotone"
                                  dataKey="count"
                                  stroke="#CE6F3F"
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </article>
                        <article className="admin-analytics__card">
                          <div className="admin-analytics__card-header">
                            <div>
                              <h3>No-shows</h3>
                            </div>
                          </div>
                          <div className="admin-chart__canvas">
                            <ResponsiveContainer width="100%" height={220}>
                              <BarChart data={analyticsNoShows}>
                                <CartesianGrid
                                  strokeDasharray="4 6"
                                  stroke="rgba(15, 45, 46, 0.12)"
                                />
                                <XAxis dataKey="label" tickLine={false} axisLine={false} />
                                <YAxis tickLine={false} axisLine={false} />
                                <Tooltip isAnimationActive={false} />
                                <Bar dataKey="count" fill="#6D77A1" radius={[6, 6, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </article>
                        <article className="admin-analytics__card">
                          <div className="admin-analytics__card-header">
                            <div>
                              <h3>Avg pickup delay</h3>
                              <p>Minutes after start</p>
                            </div>
                          </div>
                          <div className="admin-chart__canvas">
                            <ResponsiveContainer width="100%" height={220}>
                              <LineChart data={analyticsPickupDelay}>
                                <CartesianGrid
                                  strokeDasharray="4 6"
                                  stroke="rgba(15, 45, 46, 0.12)"
                                />
                                <XAxis dataKey="label" tickLine={false} axisLine={false} />
                                <YAxis tickLine={false} axisLine={false} />
                                <Tooltip
                                  isAnimationActive={false}
                                  formatter={(value) => `${Math.round(value)} min`}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="value"
                                  stroke="#176D7A"
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </article>
                        <article className="admin-analytics__card">
                          <div className="admin-analytics__card-header">
                            <div>
                              <h3>Refund reasons</h3>
                              <p>Share of refunds</p>
                            </div>
                          </div>
                          <ul className="admin-analytics__reason-list">
                            {analyticsRefundReasons.length === 0 ? (
                              <li className="admin-empty">No refund data.</li>
                            ) : (
                              analyticsRefundReasons.map((reason) => (
                                <li key={reason.reason} className="admin-analytics__reason-row">
                                  <span>{reason.reason}</span>
                                  <div className="admin-analytics__reason-bar">
                                    <span
                                      style={{
                                        width: `${Math.round(
                                          (reason.count /
                                            Math.max(
                                              analyticsRefundReasons[0]?.count || 1,
                                              1
                                            )) *
                                            100
                                        )}%`,
                                      }}
                                    />
                                  </div>
                                  <strong>{reason.count}</strong>
                                </li>
                              ))
                            )}
                          </ul>
                        </article>
                        <article className="admin-analytics__card">
                          <div className="admin-analytics__card-header">
                            <div>
                              <h3>Partner reliability</h3>
                              <p>Issue-free booking rate</p>
                            </div>
                          </div>
                          <ul className="admin-analytics__rank-list">
                            {analyticsPartnerReliability.length === 0 ? (
                              <li className="admin-empty">No partner data.</li>
                            ) : (
                              analyticsPartnerReliability.map((partner) => (
                                <li key={partner.partnerId} className="admin-analytics__rank-row">
                                  <div className="admin-analytics__rank-info">
                                    <span>{partner.partnerName}</span>
                                    <small>
                                      {partner.issueCount} issues · {partner.bookingCount} bookings
                                    </small>
                                  </div>
                                  <div className="admin-analytics__rank-bar">
                                    <span
                                      style={{
                                        width: `${Math.round(
                                          (partner.reliabilityScore || 0) * 100
                                        )}%`,
                                      }}
                                    />
                                  </div>
                                  <span className="admin-analytics__rank-value">
                                    {formatPercent(partner.reliabilityScore)}
                                  </span>
                                </li>
                              ))
                            )}
                          </ul>
                        </article>
                      </div>
                    )}
	                    {analyticsSubTab === "attendance" && (
	                      <div className="admin-analytics__grid admin-analytics__grid--attendance">
	                        <article className="admin-analytics__card admin-analytics__card--wide">
	                          <div className="admin-analytics__card-header">
	                            <div>
	                              <h3>Page visitors</h3>
                              <p>{analyticsRangeLabel}</p>
                            </div>
                            <div className="admin-analytics__card-meta">
                              <span>
                                {analyticsAttendanceTotals.totalPageVisits.toLocaleString()} visits
                              </span>
                              <span>
                                {analyticsAttendanceTotals.totalUniqueVisitors.toLocaleString()} unique
                              </span>
                            </div>
                          </div>
                          <div className="admin-chart__canvas">
                            {analyticsAttendanceSeries.length === 0 ? (
                              <p className="admin-empty">No attendance data yet.</p>
                            ) : (
                              <ResponsiveContainer width="100%" height={260}>
                                <LineChart data={analyticsAttendanceSeries}>
                                  <CartesianGrid
                                    strokeDasharray="4 6"
                                    stroke="rgba(15, 45, 46, 0.12)"
                                  />
                                  <XAxis
                                    dataKey="label"
                                    tickLine={false}
                                    axisLine={false}
                                    interval="preserveStartEnd"
                                    tick={{ fontSize: 12, fill: "#6B7C7D" }}
                                  />
                                  <YAxis
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 12, fill: "#6B7C7D" }}
                                  />
                                  <Tooltip isAnimationActive={false} />
                                  <Legend verticalAlign="top" height={26} />
                                  <Line
                                    type="monotone"
                                    dataKey="pageVisits"
                                    stroke="#208E76"
                                    strokeWidth={2.6}
                                    dot={false}
                                    name="Page visits"
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="uniqueVisitors"
                                    stroke="rgb(109, 119, 161)"
                                    strokeWidth={2.6}
                                    dot={false}
                                    name="Unique visitors"
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </article>

	                        <article className="admin-analytics__card">
	                          <div className="admin-analytics__card-header">
	                            <div>
	                              <h3>Device breakdown</h3>
	                              <p>Users by device</p>
	                            </div>
	                          </div>
	                          <div className="admin-chart__canvas">
	                            {analyticsAttendanceTotals.deviceBreakdown.length === 0 ? (
	                              <p className="admin-empty">No device data yet.</p>
	                            ) : (
	                              <ResponsiveContainer width="100%" height={260}>
	                                <BarChart
	                                  data={analyticsAttendanceTotals.deviceBreakdown}
	                                  barSize={DEVICE_BAR_SIZE}
	                                  barCategoryGap={36}
	                                  margin={{ top: 6, right: 12, left: 0, bottom: 6 }}
	                                >
	                                  <defs>
	                                    <linearGradient id="deviceFill" x1="0" y1="0" x2="1" y2="0">
	                                      <stop offset="0%" stopColor="#208E76" stopOpacity={0.9} />
	                                      <stop offset="100%" stopColor="#176D7A" stopOpacity={0.95} />
	                                    </linearGradient>
	                                  </defs>
	                                  <CartesianGrid
	                                    strokeDasharray="4 6"
	                                    stroke="rgba(15, 45, 46, 0.12)"
	                                  />
	                                  <XAxis
	                                    dataKey="label"
	                                    type="category"
	                                    tickLine={false}
	                                    axisLine={false}
	                                    interval={0}
	                                    tick={{ fontSize: 12, fill: "#6B7C7D" }}
	                                  />
	                                  <YAxis
	                                    type="number"
	                                    tickLine={false}
	                                    axisLine={false}
	                                    tick={{ fontSize: 12, fill: "#6B7C7D" }}
	                                  />
		                                  <Tooltip
		                                    isAnimationActive={false}
		                                    cursor={<DeviceBarCursor />}
		                                    formatter={(value, name, props) => {
		                                      const share = props?.payload?.share ?? 0;
		                                      return [
		                                        `${Number(value).toLocaleString()} (${share}%)`,
	                                        "Users",
	                                      ];
	                                    }}
	                                  />
	                                  <Bar dataKey="count" fill="url(#deviceFill)" radius={[8, 8, 8, 8]} />
	                                </BarChart>
	                              </ResponsiveContainer>
	                            )}
	                          </div>
	                        </article>

	                        <article className="admin-analytics__card">
	                          <div className="admin-analytics__card-header">
	                            <div>
	                              <h3>Traffic source</h3>
	                              <p>Unique visitors by source</p>
	                            </div>
	                          </div>
		                          <div className="admin-chart__canvas">
		                            {analyticsAttendanceTotals.sourceSeries.length === 0 ? (
		                              <p className="admin-empty">No source data yet.</p>
		                            ) : (
		                              <ResponsiveContainer width="100%" height={260}>
		                                <LineChart data={analyticsAttendanceTotals.sourceSeries}>
		                                  <CartesianGrid
		                                    strokeDasharray="4 6"
		                                    stroke="rgba(15, 45, 46, 0.12)"
		                                  />
		                                  <XAxis
		                                    dataKey="label"
		                                    tickLine={false}
		                                    axisLine={false}
		                                    interval="preserveStartEnd"
		                                    tick={{ fontSize: 12, fill: "#6B7C7D" }}
		                                  />
		                                  <YAxis
		                                    tickLine={false}
		                                    axisLine={false}
		                                    allowDecimals={false}
		                                    tick={{ fontSize: 12, fill: "#6B7C7D" }}
		                                  />
		                                  <Tooltip isAnimationActive={false} />
		                                  <Legend verticalAlign="top" height={26} />
		                                  <Line
		                                    type="monotone"
		                                    dataKey="instagram"
		                                    stroke="#208E76"
		                                    strokeWidth={2.6}
		                                    dot={false}
		                                    name="Instagram"
		                                  />
		                                  <Line
		                                    type="monotone"
		                                    dataKey="tiktok"
		                                    stroke="rgb(109, 119, 161)"
		                                    strokeWidth={2.6}
		                                    dot={false}
		                                    name="TikTok"
		                                  />
		                                  <Line
		                                    type="monotone"
		                                    dataKey="other"
		                                    stroke="#176D7A"
		                                    strokeWidth={2.6}
		                                    dot={false}
		                                    name="Other"
		                                  />
		                                </LineChart>
		                              </ResponsiveContainer>
		                            )}
		                          </div>
	                        </article>

	                        <article className="admin-analytics__card">
	                          <div className="admin-analytics__card-header">
	                            <div>
	                              <h3>Top pages</h3>
	                              <p>Most visited routes in the selected period</p>
	                            </div>
	                          </div>
	                          <div className="admin-analytics__list">
	                            {analyticsAttendanceTotals.topPages.length === 0 ? (
	                              <p className="admin-empty">No page routing data yet.</p>
	                            ) : (
	                              analyticsAttendanceTotals.topPages.map((page) => (
	                                <div key={page.key} className="admin-analytics__list-row">
	                                  <div className="admin-analytics__list-copy">
	                                    <strong>{page.label}</strong>
	                                    <small>{page.path}</small>
	                                  </div>
	                                  <div className="admin-analytics__list-stats">
	                                    <span>{page.visits.toLocaleString()} visits</span>
	                                    <span>{page.uniqueVisitors.toLocaleString()} unique</span>
	                                  </div>
	                                </div>
	                              ))
	                            )}
	                          </div>
	                        </article>

	                        <article className="admin-analytics__card admin-analytics__card--wide">
	                          <div className="admin-analytics__card-header">
	                            <div>
	                              <h3>Recent visits</h3>
	                              <p>Newest page visits first</p>
	                            </div>
	                          </div>
	                          {analyticsAttendanceTotals.recentVisits.length === 0 ? (
	                            <p className="admin-empty">No recent page visits yet.</p>
	                          ) : (
	                            <div className="admin-analytics__visit-table">
	                              <div className="admin-analytics__visit-head">
	                                <span>Time</span>
	                                <span>Page</span>
	                                <span>Device</span>
	                                <span>Source</span>
	                              </div>
	                              {analyticsAttendanceTotals.recentVisits.map((visit) => (
	                                <div key={visit.key} className="admin-analytics__visit-row">
	                                  <span>{visit.occurredLabel}</span>
	                                  <span>{visit.label}</span>
	                                  <span>{visit.deviceType}</span>
	                                  <span>{visit.source}</span>
	                                </div>
	                              ))}
	                            </div>
	                          )}
	                        </article>
	                      </div>
                    )}
	                  </>
	                )}
              </section>
            </div>
            
);

export default AdminAnalyticsTab;
